// src/components/suppliers/SupplierCreationModal.jsx
import React, { useState, useEffect } from 'react';
import { Modal, Form, Input, Select, Button, message, Spin, Row, Col, Divider, Tag, Popconfirm } from 'antd';
import { SearchOutlined, CheckCircleOutlined, CloseCircleOutlined, ExclamationCircleOutlined } from '@ant-design/icons';
import { createSupplier, searchSupplierByCuit } from '../../services/supplierService';
import { validateCuitAFIP } from '../../services/cuitService';
import { getAllBrands, getAllCategories } from '../../services/brandsService';

const { Option } = Select;

const SupplierCreationModal = ({ visible, onClose, onSupplierCreated }) => {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(false);
    const [validatingCuit, setValidatingCuit] = useState(false);
    const [cuitValidated, setCuitValidated] = useState(false);
    const [afipData, setAfipData] = useState(null);
    const [bypassMode, setBypassMode] = useState(false); // New: bypass CUIT validation mode
    
    // Brands and Categories state
    const [brands, setBrands] = useState([]);
    const [categories, setCategories] = useState([]);
    const [loadingBrands, setLoadingBrands] = useState(true);

    // Fetch brands and categories on component mount
    useEffect(() => {
        const fetchBrandsAndCategories = async () => {
            try {
                setLoadingBrands(true);
                const [brandsData, categoriesData] = await Promise.all([
                    getAllBrands(),
                    getAllCategories()
                ]);
                console.log('Brands loaded:', brandsData.length, brandsData);
                console.log('Categories loaded:', categoriesData.length, categoriesData);
                setBrands(brandsData);
                setCategories(categoriesData);
            } catch (error) {
                console.error('Error fetching brands/categories:', error);
                message.warning('No se pudieron cargar las marcas o categorías');
            } finally {
                setLoadingBrands(false);
            }
        };

        if (visible) {
            fetchBrandsAndCategories();
        }
    }, [visible]);

    // Handle CUIT validation with AFIP
    const handleValidateCuit = async () => {
        const cuit = form.getFieldValue('documento_nro');
        
        if (!cuit || cuit.trim().length === 0) {
            message.warning('Por favor ingrese un CUIT');
            return;
        }

        // Clean CUIT (remove hyphens and spaces)
        const cleanCuit = cuit.replace(/[-\s]/g, '');
        
        if (cleanCuit.length < 10 || cleanCuit.length > 11) {
            message.error('CUIT debe tener 10-11 dígitos');
            return;
        }

        setValidatingCuit(true);
        setCuitValidated(false);
        setAfipData(null);

        try {
            // First check if supplier already exists in local DB (404 is expected for new suppliers)
            let existingSupplier = null;
            try {
                existingSupplier = await searchSupplierByCuit(cleanCuit);
            } catch (err) {
                // Ignore errors from local search - supplier doesn't exist locally, which is fine
                console.log('Supplier not found locally (expected for new suppliers)');
            }
            
            if (existingSupplier) {
                message.warning(`Proveedor ya existe en la base de datos: ${existingSupplier.razon_social}`);
                setValidatingCuit(false);
                return;
            }

            // Validate with AFIP using the same service as invoice preview
            const afipData = await validateCuitAFIP(cleanCuit);
            
            if (afipData && afipData.razon_social) {
                message.success('CUIT válido en AFIP - Proveedor encontrado');
                setCuitValidated(true);
                setAfipData(afipData);
                
                // Pre-fill form with AFIP data
                form.setFieldsValue({
                    documento_nro: afipData.documento_nro || cleanCuit,
                    razon_social: afipData.razon_social,
                    condicion_impositiva: afipData.condicion_impositiva,
                    direccion: afipData.direccion || '',
                    localidad: afipData.localidad || '',
                    provincia: afipData.provincia || '',
                    codigopostal: afipData.codigopostal || ''
                });
            } else {
                message.error('CUIT no encontrado en AFIP');
                setCuitValidated(false);
            }
        } catch (error) {
            console.error('Error validating CUIT with AFIP:', error);
            
            // Check if it's an AFIP validation error
            if (error.response && error.response.data) {
                message.error(error.response.data.message || 'Error al validar CUIT con AFIP');
            } else {
                message.error('Error al conectar con el servicio de validación AFIP');
            }
            setCuitValidated(false);
        } finally {
            setValidatingCuit(false);
        }
    };

    // Handle bypass CUIT validation
    const handleBypassCuit = () => {
        setBypassMode(true);
        setCuitValidated(true); // Allow form to show
        setAfipData(null);
        message.info('Modo sin CUIT activado - Complete los datos manualmente');
    };

    // Handle form submission
    const handleSubmit = async () => {
        try {
            const values = await form.validateFields();
            
            // In bypass mode, only razon_social is required (validated by form rules)
            if (!cuitValidated && !bypassMode) {
                message.warning('Por favor valide el CUIT con AFIP primero o use "Crear sin CUIT"');
                return;
            }

            setLoading(true);

            // Build payload - handle bypass mode with optional fields
            const supplierPayload = {
                documento_nro: bypassMode ? '' : (values.documento_nro?.replace(/[-\s]/g, '') || ''),
                documento_tipo: bypassMode ? '' : 'CUIT',
                razon_social: values.razon_social,
                condicion_impositiva: bypassMode ? '' : (values.condicion_impositiva || ''),
                direccion: values.direccion || '',
                localidad: values.localidad || '',
                provincia: values.provincia || '',
                codigopostal: values.codigopostal || '',
                email: values.email || '',
                telefono: values.telefono || '',
                estado: 'ACTIVO',
                related_brands: values.related_brands || [],
                related_categories: values.related_categories || [],
                // New field to indicate this supplier was created without CUIT validation
                created_without_cuit: bypassMode
            };

            // Create supplier
            const response = await createSupplier(supplierPayload);

            if (response.success) {
                message.success(bypassMode ? 'Proveedor creado sin CUIT' : 'Proveedor creado exitosamente');
                form.resetFields();
                setCuitValidated(false);
                setAfipData(null);
                setBypassMode(false);
                onSupplierCreated(response.supplier);
                onClose();
            }
        } catch (error) {
            console.error('Error creating supplier:', error);
            
            if (error.response && error.response.data) {
                const errorData = error.response.data;
                if (errorData.error === 'Supplier already exists') {
                    message.error(`Proveedor ya existe: ${errorData.existingSupplier?.razon_social || ''}`);
                } else {
                    message.error(errorData.message || 'Error al crear proveedor');
                }
            } else {
                message.error('Error al crear proveedor');
            }
        } finally {
            setLoading(false);
        }
    };

    // Handle modal close
    const handleCancel = () => {
        form.resetFields();
        setCuitValidated(false);
        setAfipData(null);
        setBypassMode(false);
        onClose();
    };

    return (
        <Modal
            title="Crear Nuevo Proveedor"
            open={visible}
            onCancel={handleCancel}
            width={750}
            style={{ top: 5 }}
            bodyStyle={{ paddingTop: 6, paddingBottom: 6 }}
            footer={
                <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8 }}>
                    <Button key="cancel" onClick={handleCancel}>
                        Cancelar
                    </Button>
                    {/* Bypass CUIT Button - Only show when not validated and not in bypass mode */}
                    {!cuitValidated && !bypassMode && !validatingCuit && (
                        <Popconfirm
                            title="¿Crear proveedor sin CUIT?"
                            description={
                                <div style={{ maxWidth: 280 }}>
                                    <p>El proveedor se creará sin validación AFIP.</p>
                                    <p style={{ color: '#ff7a45', marginBottom: 0 }}>
                                        <ExclamationCircleOutlined /> CUIT y Condición Impositiva quedarán vacíos.
                                    </p>
                                </div>
                            }
                            onConfirm={handleBypassCuit}
                            okText="Sí, continuar"
                            cancelText="Cancelar"
                            icon={<ExclamationCircleOutlined style={{ color: '#faad14' }} />}
                        >
                            <Button
                                type="default"
                                icon={<ExclamationCircleOutlined />}
                                style={{ 
                                    color: '#faad14', 
                                    borderColor: '#faad14',
                                    background: '#fffbe6'
                                }}
                            >
                                Crear sin CUIT
                            </Button>
                        </Popconfirm>
                    )}
                    <Button
                        key="submit"
                        type="primary"
                        loading={loading}
                        onClick={handleSubmit}
                        disabled={!cuitValidated && !bypassMode}
                    >
                        {bypassMode ? 'Crear sin CUIT' : 'Crear Proveedor'}
                    </Button>
                </div>
            }
        >
            <Form
                form={form}
                layout="vertical"
                autoComplete="off"
                size="small"
            >
                <Divider orientation="left" style={{ margin: '2px 0 6px 0' }}>
                    {bypassMode ? 'Modo Sin CUIT' : 'Validación CUIT'}
                </Divider>
                
                {!bypassMode && (
                    <Row gutter={16}>
                        <Col span={16}>
                            <Form.Item
                                name="documento_nro"
                                label="CUIT"
                                rules={[
                                    { required: !bypassMode, message: 'Por favor ingrese el CUIT' },
                                    { pattern: /^[0-9-]{10,13}$/, message: 'CUIT inválido' }
                                ]}
                            >
                                <Input
                                    placeholder="20-12345678-9"
                                    maxLength={13}
                                    disabled={cuitValidated}
                                    suffix={
                                        cuitValidated ? (
                                            <CheckCircleOutlined style={{ color: '#52c41a' }} />
                                        ) : (
                                            <CloseCircleOutlined style={{ color: '#d9d9d9' }} />
                                        )
                                    }
                                />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item label=" ">
                                <Button
                                    type="primary"
                                    icon={<SearchOutlined />}
                                    onClick={handleValidateCuit}
                                    loading={validatingCuit}
                                    disabled={cuitValidated}
                                    block
                                >
                                    Validar AFIP
                                </Button>
                            </Form.Item>
                        </Col>
                    </Row>
                )}

                {/* Bypass mode indicator */}
                {bypassMode && (
                    <div style={{ 
                        textAlign: 'center', 
                        padding: '8px 12px', 
                        background: '#fff7e6', 
                        border: '1px solid #ffd591',
                        borderRadius: 6,
                        marginBottom: 12 
                    }}>
                        <ExclamationCircleOutlined style={{ color: '#fa8c16', marginRight: 8 }} />
                        <span style={{ color: '#d46b08' }}>
                            Modo sin CUIT - Complete solo la Razón Social (obligatoria)
                        </span>
                    </div>
                )}

                {(cuitValidated || bypassMode) && (
                    <>
                        <Divider orientation="left" style={{ margin: '8px 0' }}>Información del Proveedor</Divider>

                        <Form.Item
                            name="razon_social"
                            label="Razón Social"
                            rules={[{ required: true, message: 'Por favor ingrese la razón social' }]}
                            style={{ marginBottom: 12 }}
                        >
                            <Input placeholder={bypassMode ? "Nombre del proveedor informal" : "Empresa SA"} />
                        </Form.Item>

                        {!bypassMode && (
                            <Form.Item
                                name="condicion_impositiva"
                                label="Condición Impositiva"
                                rules={[{ required: !bypassMode, message: 'Por favor seleccione la condición impositiva' }]}
                                style={{ marginBottom: 12 }}
                            >
                                <Select placeholder="Seleccione condición impositiva">
                                    <Option value="RESPONSABLE INSCRIPTO">Responsable Inscripto</Option>
                                    <Option value="MONOTRIBUTO">Monotributo</Option>
                                    <Option value="EXENTO">Exento</Option>
                                    <Option value="CONSUMIDOR FINAL">Consumidor Final</Option>
                                </Select>
                            </Form.Item>
                        )}

                        <Divider orientation="left" style={{ margin: '8px 0' }}>Datos de Contacto</Divider>

                        <Form.Item
                            name="direccion"
                            label="Dirección"
                            style={{ marginBottom: 12 }}
                        >
                            <Input placeholder="Av. Corrientes 1234" />
                        </Form.Item>

                        <Row gutter={16}>
                            <Col span={12}>
                                <Form.Item
                                    name="localidad"
                                    label="Localidad"
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input placeholder="CABA" />
                                </Form.Item>
                            </Col>
                            <Col span={12}>
                                <Form.Item
                                    name="provincia"
                                    label="Provincia"
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input placeholder="Buenos Aires" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Row gutter={16}>
                            <Col span={8}>
                                <Form.Item
                                    name="codigopostal"
                                    label="Código Postal"
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input placeholder="1043" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="telefono"
                                    label="Teléfono"
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input placeholder="011-1234-5678" />
                                </Form.Item>
                            </Col>
                            <Col span={8}>
                                <Form.Item
                                    name="email"
                                    label="Email"
                                    rules={[{ type: 'email', message: 'Email inválido' }]}
                                    style={{ marginBottom: 12 }}
                                >
                                    <Input placeholder="email@ejemplo.com" />
                                </Form.Item>
                            </Col>
                        </Row>

                        <Divider orientation="left" style={{ margin: '8px 0' }}>Marcas y Categorías Relacionadas</Divider>

                        <Form.Item
                            name="related_brands"
                            label="Marcas Relacionadas"
                            help="Seleccione las marcas que este proveedor suministra"
                            style={{ marginBottom: 12 }}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Seleccione marcas"
                                loading={loadingBrands}
                                disabled={loadingBrands}
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    option.label.toLowerCase().includes(input.toLowerCase())
                                }
                                options={brands}
                                tagRender={(props) => {
                                    const { label, closable, onClose } = props;
                                    const colors = ['blue', 'cyan', 'geekblue', 'purple', 'magenta'];
                                    const colorIndex = brands.findIndex(b => b.label === label) % colors.length;
                                    return (
                                        <Tag
                                            color={colors[colorIndex]}
                                            closable={closable}
                                            onClose={onClose}
                                            style={{ marginRight: 3 }}
                                        >
                                            {label}
                                        </Tag>
                                    );
                                }}
                            />
                        </Form.Item>

                        <Form.Item
                            name="related_categories"
                            label="Categorías Relacionadas"
                            help="Seleccione las categorías de productos que suministra"
                            style={{ marginBottom: 8 }}
                        >
                            <Select
                                mode="multiple"
                                placeholder="Seleccione categorías"
                                loading={loadingBrands}
                                disabled={loadingBrands}
                                allowClear
                                showSearch
                                filterOption={(input, option) =>
                                    option?.label?.toLowerCase().includes(input.toLowerCase())
                                }
                                options={categories}
                                notFoundContent={loadingBrands ? 'Cargando...' : 'No hay categorías disponibles'}
                                tagRender={(props) => {
                                    const { label, closable, onClose } = props;
                                    return (
                                        <Tag
                                            color="green"
                                            closable={closable}
                                            onClose={onClose}
                                            style={{ marginRight: 3 }}
                                        >
                                            {label}
                                        </Tag>
                                    );
                                }}
                            />
                        </Form.Item>
                    </>
                )}

                {validatingCuit && (
                    <div style={{ textAlign: 'center', padding: '20px' }}>
                        <Spin tip="Validando CUIT con AFIP..." />
                    </div>
                )}

                {!cuitValidated && !validatingCuit && !bypassMode && (
                    <div style={{ textAlign: 'center', padding: '12px', color: '#999' }}>
                        Ingrese un CUIT y haga clic en "Validar AFIP" para continuar,<br/>
                        o use "Crear sin CUIT" para proveedores informales
                    </div>
                )}
            </Form>
        </Modal>
    );
};

export default SupplierCreationModal;
